import {
  Avatar,
  Box,
  Button,
  Card,
  CircularProgress,
  Grid,
  Typography,
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { collection, getDocs } from "firebase/firestore";
import { Link as RouterLink } from "react-router-dom";
import MainLayout from "../../layouts/main_layout/main_layout";
import { firestore } from "../../index";
import {
  MAIN_THEME_COLOR,
  SECONDARY_THEME_COLOR,
} from "../../providers/theme/colors/colors";
import {
  DEANERY_OPTIONS as deaneryOrder,
  DeaneryMember,
  defaultDeaneryMembers,
  normalizeDeaneryMember,
  sortDeaneryMembers,
} from "./deanery_data";

function DeaneryProfileCard({ member }: { member: DeaneryMember }) {
  return (
    <Card
      sx={{
        height: "100%",
        p: 2,
        borderRadius: 4,
        border: `1px solid ${MAIN_THEME_COLOR}33`,
        background:
          "linear-gradient(180deg, rgba(247, 251, 255, 0.98) 0%, rgba(255, 255, 255, 1) 100%)",
        boxShadow: "0 12px 24px rgba(64, 162, 227, 0.08)",
      }}
    >
      <Box display="flex" flexDirection="column" alignItems="center" textAlign="center">
        {member.img ? (
          <Box
            component="img"
            src={member.img}
            alt={member.name}
            sx={{
              width: { xs: 150, sm: 170 },
              height: { xs: 180, sm: 200 },
              borderRadius: 3,
              objectFit: "cover",
              objectPosition: "center top",
              border: `3px solid ${SECONDARY_THEME_COLOR}`,
            }}
          />
        ) : (
          <Avatar
            sx={{
              width: { xs: 150, sm: 170 },
              height: { xs: 180, sm: 200 },
              borderRadius: 3,
              bgcolor: `${SECONDARY_THEME_COLOR}33`,
              color: MAIN_THEME_COLOR,
            }}
          >
            {member.name.charAt(0)}
          </Avatar>
        )}
        <Typography mt={1.5} variant="h6" fontWeight={700}>
          {member.name}
        </Typography>
        {member.place ? (
          <Typography variant="body2" color="text.secondary" mt={0.5}>
            {member.place}
          </Typography>
        ) : null}
        <Typography
          variant="body2"
          color="text.primary"
          fontWeight={700}
          mt={0.75}
          sx={{ fontSize: "0.9rem", lineHeight: 1.5 }}
        >
          {member.designation}
        </Typography>
      </Box>
    </Card>
  );
}

export default function DeaneryYouthCommissionPage() {
  const [members, setMembers] = useState<DeaneryMember[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchMembers() {
      try {
        const snapshot = await getDocs(collection(firestore, "deanery_members"));
        const deaneryMembers = sortDeaneryMembers(snapshot.docs.map((doc) => normalizeDeaneryMember({
          docId: doc.id,
          ...(doc.data() as Omit<DeaneryMember, "docId">),
        })));
        setMembers(deaneryMembers.length ? deaneryMembers : sortDeaneryMembers(defaultDeaneryMembers));
      } catch (error) {
        console.error("Failed to load deanery members", error);
        setMembers(sortDeaneryMembers(defaultDeaneryMembers));
      } finally {
        setLoading(false);
      }
    }

    fetchMembers();
  }, []);

  const groupedMembers = useMemo(() => {
    return deaneryOrder.map((deanery) => ({
      deanery,
      members: sortDeaneryMembers(members.filter((member) => member.deanery === deanery)),
    }));
  }, [members]);

  return (
    <MainLayout>
      <Box my={{ xs: 4, md: 6 }}>
        <Box textAlign="center" mb={4}>
          <Typography variant="h4" fontWeight={800} color={MAIN_THEME_COLOR}>
            Deanery Youth Commission
          </Typography>
          <Typography variant="body1" color="text.secondary" mt={1}>
            The five deaneries with ICYM and YCS directors and presidents.
          </Typography>
        </Box>

        <Card
          sx={{
            mb: 4,
            p: { xs: 2.5, md: 3.5 },
            borderRadius: 4,
            border: `1px solid ${SECONDARY_THEME_COLOR}55`,
            background: `linear-gradient(135deg, ${MAIN_THEME_COLOR} 0%, ${SECONDARY_THEME_COLOR} 100%)`,
            color: "#ffffff",
            boxShadow: "0 18px 36px rgba(64, 162, 227, 0.18)",
          }}
        >
          <Grid container spacing={2} alignItems="center" justifyContent="space-between">
            <Grid item xs={12} md={8}>
              <Typography variant="overline" sx={{ opacity: 0.9, letterSpacing: 1.4 }}>
                Event Registration
              </Typography>
              <Typography variant="h5" fontWeight={800} mt={0.5}>
                Yuva Dabazo
              </Typography>
              <Typography variant="body1" mt={1} sx={{ opacity: 0.92, maxWidth: 680 }}>
                Access the Yuva Dabazo event registration directly from the Deanery Youth
                Commission section.
              </Typography>
            </Grid>
            <Grid item xs={12} md="auto">
              <Button
                component={RouterLink}
                to="/yuvadabazo"
                variant="contained"
                size="large"
                sx={{
                  px: 3,
                  py: 1.2,
                  fontWeight: 700,
                  color: MAIN_THEME_COLOR,
                  backgroundColor: "#ffffff",
                  "&:hover": {
                    backgroundColor: "#f5f9ff",
                  },
                }}
              >
                Open Registration
              </Button>
            </Grid>
          </Grid>
        </Card>

        {loading ? (
          <Box display="flex" justifyContent="center" py={8}>
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={3}>
            {groupedMembers.map((group) => (
              <Grid item xs={12} key={group.deanery}>
                <Card
                  sx={{
                    p: { xs: 2, md: 3 },
                    borderRadius: 4,
                    border: `1px solid ${MAIN_THEME_COLOR}22`,
                    boxShadow: "0 14px 28px rgba(64, 162, 227, 0.08)",
                  }}
                >
                  <Box
                    mb={2.5}
                    px={2}
                    py={1}
                    sx={{
                      width: "fit-content",
                      borderRadius: 999,
                      color: "#fff",
                      background: `linear-gradient(90deg, ${MAIN_THEME_COLOR} 0%, ${SECONDARY_THEME_COLOR} 100%)`,
                    }}
                  >
                    <Typography variant="h6" fontWeight={800}>
                      {group.deanery}
                    </Typography>
                  </Box>

                  <Grid container spacing={3}>
                    {group.members.map((member) => (
                      <Grid item xs={12} sm={6} md={4} key={`${group.deanery}-${member.name}-${member.designation}`}>
                        <DeaneryProfileCard member={member} />
                      </Grid>
                    ))}
                  </Grid>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>
    </MainLayout>
  );
}
